import "./style.scss";

import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import BlockOutlined from "@mui/icons-material/BlockOutlined";
import { Chip } from "@mui/material";
import { getUserRolesLabel } from "components/MemberList/userRoles";
import ModalBox from "components/Modal";
import CopyToClipboardButton from "components/shared/CopyToClipboardButton";
import DeleteModal from "components/shared/DeleteModal";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import StyledButton from "components/shared/StyledButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import { tagsColors } from "constants/colors";
import type { ROLES } from "containers/ScopeContext/roles.enum";
import { extractCustomRoleId, formatLocaleDateString, isCustomRole } from "helpers/utils";
import { useAbility } from "hooks/useAbility";
import { usePossibleUserRoles } from "hooks/usePossibleUserRoles";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type { APIKey } from "store/settingsStore";
import { deleteOneAPIKey, fetchAPIKeys, generateAPIKey } from "store/settingsStore";
import type { AppDispatch, RootState } from "store/store";

import variables from "../../assets/styles/_variables.module.scss";
import ApiKeyGuide from "./ApiKeyGuide";
import { canCreateApiKey } from "./canCreateApiKey";
import CreateKeyModal from "./CreateKeyModal";

const APIKeysSettings = () => {
    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const ability = useAbility();
    const { apiKeys, userData } = useSelector((state: RootState) => state.settings);
    const canCreateKey = canCreateApiKey(userData, ability);
    const { possibleUserRoles, rolesFetchStatus } = usePossibleUserRoles();
    const [showRevokeModal, setShowRevokeModal] = useState<boolean | number>(false);
    const [showGenerateModal, setShowGenerateModal] = useState<boolean>(false);
    const [keepKeyModalOpen, setKeepKeyModalOpen] = useState(false);

    useEffect(() => {
        dispatch(fetchAPIKeys());
    }, [dispatch]);

    const formatApiKeyDate = (value: Date | string | null | undefined) => {
        if (!value) return "";
        return formatLocaleDateString({
            date: new Date(value),
            options: { numeric: true, timeZone: "UTC" },
        });
    };

    const labelForKeyScope = (value: unknown) => {
        if (typeof value !== "string") return "";
        if (isCustomRole(value)) {
            const roleId = extractCustomRoleId(value);
            return possibleUserRoles.find((role) => role.id === roleId)?.label ?? value;
        }
        return possibleUserRoles.find((role) => role.role === value)?.label ?? getUserRolesLabel(value as ROLES);
    };

    const columns: LastElementTableColumns[] = [
        {
            id: "name",
            label: t("Settings.APIKey.Name"),
            align: "left",
            subtitle: "email",
            avatar: false,
        },
        {
            id: "sample",
            label: t("Settings.APIKey.Sample"),
            align: "left",
            format: (value, id) => {
                const apiKey = apiKeys.find((key) => key.id == id);
                const createdAt = formatApiKeyDate(apiKey?.createdAt) || "-";

                return (
                    <>
                        <StyledTextField
                            className="apikey-input"
                            size="small"
                            value={value}
                            disabled
                            fullWidth
                            InputProps={{
                                endAdornment: !!apiKey?.key && (
                                    <CopyToClipboardButton
                                        color={""}
                                        value={apiKey?.sample as string}
                                        style={{ height: 16, width: 16, color: variables["foreground"] }}
                                    />
                                ),
                            }}
                        />
                        <span className="last-element-table-cell-text-subtitle apikey-input-meta">
                            {t("Settings.APIKey.CreatedAt")} {createdAt}
                        </span>
                    </>
                );
            },
        },
        {
            id: "keyScope",
            label: t("Settings.APIKey.Scope"),
            align: "left",
            format: (value) => {
                const index =
                    typeof value === "string" && isCustomRole(value)
                        ? possibleUserRoles.findIndex((role) => role.id === extractCustomRoleId(value))
                        : possibleUserRoles.findIndex((role) => role.role === value);
                const color = tagsColors[(index === -1 ? 0 : index) % tagsColors.length];
                return (
                    <Chip
                        size="small"
                        label={labelForKeyScope(value)}
                        sx={{
                            backgroundColor: color.backgroundColor,
                            color: color.textColor,
                        }}
                    />
                );
            },
        },
        {
            id: "expiryDate",
            label: t("Settings.APIKey.ExpiryDate"),
            align: "left",
            format: (value) => formatApiKeyDate(value) || t("Settings.APIKey.NoExpiry"),
        },
        {
            id: "action",
            label: "",
            align: "center",
            format: (_value, id) => (
                <ThreeDotMenu
                    options={[
                        {
                            id: "revoke",
                            name: <span className="apikey-revoke">{t("Settings.APIKey.Revoke")}</span>,
                            icon: <BlockOutlined className="apikey-revoke-icon" sx={{ fontSize: 14 }} />,
                            action: () => setShowRevokeModal(id as number),
                        },
                    ]}
                />
            ),
        },
    ];

    const keyToRevoke =
        typeof showRevokeModal === "number" ? apiKeys.find((key) => key.id === showRevokeModal) : undefined;

    const handleRevokeKey = async () => {
        if (!keyToRevoke) return;
        try {
            await dispatch(deleteOneAPIKey(keyToRevoke)).unwrap();
        } catch {
            // The store already shows the error toast.
        }
    };

    const handleGenerateKey = (options: Pick<APIKey, "name" | "keyScope" | "expiryDate">) => {
        return dispatch(generateAPIKey(options)).unwrap();
    };

    return (
        <div className="apikey-settings">
            {/* Revoke key modal */}
            <DeleteModal
                open={showRevokeModal !== false}
                onClose={() => setShowRevokeModal(false)}
                onDelete={handleRevokeKey}
                elementTitle={keyToRevoke?.name ?? ""}
                needsDoubleQuotes
                description={t("Settings.APIKey.Modals.DeleteKey.Impact")}
                confirmLabel={t("Settings.APIKey.Revoke")}
            />

            {/* Generate key modal */}
            <ModalBox
                onClose={() => {
                    if (keepKeyModalOpen) return;
                    setShowGenerateModal(false);
                }}
                mainTitle={t("Settings.APIKey.Modals.CreateKey.Title")}
                open={showGenerateModal !== false}
                showCloseButton={!keepKeyModalOpen}
                blockOutsideClose={keepKeyModalOpen}
            >
                <CreateKeyModal
                    close={() => {
                        setKeepKeyModalOpen(false);
                        setShowGenerateModal(false);
                    }}
                    handleCreateKey={handleGenerateKey}
                    roles={possibleUserRoles}
                    rolesReady={rolesFetchStatus === "succeeded"}
                    onKeepOpenChange={setKeepKeyModalOpen}
                />
            </ModalBox>

            {/* Actual page */}
            <div className="apikey-settings-header">
                <h1 className="apikey-settings-title">{t("Settings.API")}</h1>
                {canCreateKey && (
                    <div className="apikey-settings-button">
                        <StyledButton buttonType="primary" onClick={() => setShowGenerateModal(true)}>
                            <AddCircleOutline sx={{ fontSize: "1rem", fill: "white" }} />
                            {t("Settings.APIKey.GenerateKey")}
                        </StyledButton>
                    </div>
                )}
            </div>
            <div className="apikey-settings-main">
                <ApiKeyGuide />
                <LastElementTable
                    columns={columns}
                    data={apiKeys}
                    onShowAll={() => {}}
                    max={-1}
                    dataType="apiKeys"
                    stickyFooter
                />
            </div>
        </div>
    );
};

export default APIKeysSettings;
